import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import cn from "classnames";
import styles from "./Settings.module.sass";
import ContractGeneralInfo from "./ContractGeneralInfo";
import ContractDatesDerogations from "./ContractDatesDerogations";
import ContractRemuneration from "./ContractRemuneration";
import AvantagesNature from "./AvantagesNature";
import Specificite from "./Specificite";
import Confirmation from "./Confirmation";
import { getFolderById, updateFolder } from "../../services/folder.service";
import { updateContract, getContractById } from "../../services/contract.service";
import { getStudentById } from "../../services/student.service";
import { toast } from "react-toastify";


const CompanyCheckInformation2 = () => {
  const { id: folderId } = useParams(); // Récupérer l'ID du folder depuis l'URL

  // États pour la navigation et les données
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [folderData, setFolderData] = useState({});
  const [studentData, setStudentData] = useState({});
  const [contractData, setContractData] = useState({
    generalInfo: {},
    datesDerogations: {},
    remuneration: {},
    avantagesNature: {},
    specificite: {}
  });
  const [contractId, setContractId] = useState(null);
  const [isStepValid, setIsStepValid] = useState(false);

  // Effet pour synchroniser les données du contrat après chaque étape
  useEffect(() => {
    if (contractId) {
      syncContractData();
    }
  }, [contractId, currentStep]);

  // Configuration des étapes pour la création de contrat
  const steps = [
    {
      id: 0,
      title: "Informations générales",
      component: ContractGeneralInfo,
    },
    {
      id: 1,
      title: "Dates et dérogations",
      component: ContractDatesDerogations,
    },
    {
      id: 2,
      title: "Rémunération",
      component: ContractRemuneration,
    },
    {
      id: 3,
      title: "Avantages en nature",
      component: AvantagesNature,
    },
    {
      id: 4,
      title: "Spécificités",
      component: Specificite,
    },
    {
      id: 5,
      title: "Confirmation",
      component: Confirmation,
    }
  ];


  // Récupérer les informations du dossier et de l'étudiant au chargement du composant
  useEffect(() => {
    const fetchFolderAndStudentData = async () => {
      if (!folderId) {
        setError("ID du dossier manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const folder = await getFolderById(folderId);
        setFolderData(folder);
        
        // Récupérer les informations de l'étudiant associé au dossier
        if (folder.student_id) {
          try {
            const student = await getStudentById(folder.student_id);
            setStudentData(student);
          } catch (studentError) {
            // Ne pas bloquer le processus si on ne peut pas récupérer l'étudiant
          }
        }
        
        // Récupérer les informations du contrat associé au dossier
        if (folder.contract_id) {
          setContractId(folder.contract_id);
          const contractDetails = await getContractById(folder.contract_id);
          if (contractDetails) {
            // Initialiser les données du contrat avec les données existantes
            setContractData({
              generalInfo: { ...contractDetails },
              datesDerogations: { ...contractDetails },
              remuneration: { ...contractDetails },
              avantagesNature: { ...contractDetails },
              specificite: { ...contractDetails }
            });
          }
        } else {
          setError("Aucun contrat associé à ce dossier");
          setLoading(false);
          return;
        }
        
        setError(null);
      } catch (error) {
        setError("Impossible de récupérer les informations du dossier");
      } finally {
        setLoading(false);
      }
    };

    fetchFolderAndStudentData();
  }, [folderId]);

  // Navigation entre les étapes
  const goToNextStep = async () => {
    if (currentStep < steps.length - 1) {
      setSaving(true);
      try {
        // Si on passe de l'étape 0 (Informations générales) à l'étape suivante
        if (currentStep === 0 && contractData.generalInfo && Object.keys(contractData.generalInfo).length > 0) {
          try {
            if (contractId) {
              await updateContract(contractId, contractData.generalInfo);
              toast.success('Informations générales mises à jour');
            } else {
              toast.error('Aucun contrat associé à ce dossier');
              return;
            }
          } catch (error) {
            toast.error('Erreur lors de la sauvegarde des informations générales');
            return;
          }
        }

        // Si on passe de l'étape 1 (Dates et dérogations) à l'étape suivante
        if (currentStep === 1 && contractData.datesDerogations && Object.keys(contractData.datesDerogations).length > 0) {
          try {
            if (contractId) {
              await updateContract(contractId, contractData.datesDerogations);
              toast.success('Dates et dérogations mises à jour');
            }
          } catch (error) {
            toast.error('Erreur lors de la mise à jour des dates et dérogations');
          }
        }

        // Si on passe de l'étape 2 (Rémunération) à l'étape 3 (Avantages en nature)
        if (currentStep === 2) {
          try {
            if (contractId && contractData.remuneration && Object.keys(contractData.remuneration).length > 0) {
              await updateContract(contractId, contractData.remuneration);
              toast.success('Rémunération mise à jour');
            }

          } catch (error) {
            toast.error('Erreur lors de la sauvegarde des données de rémunération');
          }
        }
        
        // Si on passe de l'étape 3 (Avantages en nature) à l'étape 4 (Spécificités)
        if (currentStep === 3) {
          try {
            if (contractId && contractData.avantagesNature && Object.keys(contractData.avantagesNature).length > 0) {
              await updateContract(contractId, contractData.avantagesNature);
              toast.success('Avantages en nature mis à jour');
            }

          } catch (error) {
            toast.error('Erreur lors de la sauvegarde des données des avantages en nature');
          }
        }

        // Si on passe de l'étape 4 (Spécificités) à l'étape 5 (Confirmation)
        if (currentStep === 4) {
          try {
            // Mettre à jour la progression du dossier : contrat créé
            try {
              const currentFolder = await getFolderById(folderId);
              const currentProgression = currentFolder.progression || {};

              const updatedProgression = {
                ...currentProgression,
                entreprise: {
                  ...currentProgression.entreprise,
                  contrat_cree: true
                }
              };

              await updateFolder(folderId, {
                progression: updatedProgression
              });

              toast.success('Contrat finalisé avec succès');
              
            } catch (progressionError) {
              toast.error('Erreur lors de la mise à jour de la progression');
            }
          } catch (error) {
            toast.error('Erreur lors de la sauvegarde des données des spécificités');
          }
        }

        setCurrentStep(currentStep + 1);
      } finally {
        setSaving(false);
      }
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Mise à jour des données du contrat et du folder
  const updateStepData = async (stepKey, data) => {
    // Gérer la validation des étapes
    if (stepKey === 'isValid') {
      setIsStepValid(data);
      return;
    }
    
    if (stepKey === 'folder') {
      // Mise à jour des données du folder
      try {
        setFolderData(prev => ({ ...prev, ...data }));
        // Sauvegarder immédiatement dans PocketBase
        await updateFolder(folderId, data);
        console.log('Folder updated with:', data);
      } catch (error) {
        console.error('Erreur lors de la mise à jour du folder:', error);
        toast.error('Erreur lors de la sauvegarde');
      }
    } else {
      // Mise à jour des données du contrat
      setContractData(prev => {
        const newData = {
          ...prev,
          [stepKey]: typeof data === 'object' && data !== null ? { ...prev[stepKey], ...data } : data
        };
        return newData;
      });
    }
  };
  
  // Fonction pour synchroniser les données du contrat avec le backend
  const syncContractData = async () => {
    if (contractId) {
      try {
        const contractDetails = await getContractById(contractId);
        if (contractDetails) {
          // Mettre à jour les données du contrat dans l'état local
          setContractData(prev => {
            return {
              ...prev,
              generalInfo: {
                ...prev.generalInfo,
                ...contractDetails
              }
            };
          });
        }
      } catch (error) {
        // Erreur silencieuse pour la synchronisation
      }
    }
  };

  // Rendu du composant de l'étape actuelle
  const CurrentStepComponent = steps[currentStep].component;

  // Affichage de chargement
  if (loading) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Chargement des informations du dossier...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            <p>Erreur : {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      {/* Navigation par étapes */}
      <div className={styles.menu}>
        {steps.map((step, index) => (
          <button
            className={cn(styles.button, {
              [styles.active]: currentStep === index,
              [styles.completed]: currentStep > index,
            })}
            key={step.id}
          >
            <span className={styles.stepNumber}>{index + 1}</span>
            {step.title}
          </button>
        ))}
      </div>

      {/* Contenu de l'étape actuelle */}
      <div className={styles.wrapper}>
        <div className={styles.stepContent}>
          <div className={styles.componentWrapper}>
            <CurrentStepComponent
              data={{ folder: folderData, contract: contractData, student: studentData }}
              updateData={updateStepData}
              currentStep={currentStep}
            />
          </div>
        </div>

        {/* Boutons de navigation */}
        {currentStep < steps.length - 1 && (
          <div className={styles.navigationButtons}>
            <button
              className="button-stroke"
              onClick={goToPreviousStep}
              disabled={currentStep === 0 || saving}
            >
              Précédent
            </button>

            <button
              className="button"
              onClick={goToNextStep}
              disabled={saving || !isStepValid}
            >
              Terminer
            </button>
          </div>
        )}


      </div>
    </div>
  );
};

export default CompanyCheckInformation2;
